import kemuCore from '@kemu-io/kemu-core';
import { findRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import { type Recipe, type RecipeThing, type RecipeWidget, type WidgetState, type WidgetGroupState, type GateChild, type GateCanvasInfo, type RecipeCanvasInfo, WidgetType } from '@kemu-io/kemu-core/types';
import type { Snapshot } from './types';
import {
  removeWidget,
  registerChildGate,
  removeChildGate,
  setGateCanvasInfo,
  setBlockCanvasInfo,
  setCanvasInfo,
  editCustomWidgetState,
  updateRecipeCacheMeta,
  getWidgetDisabled,
  disableWidget,
} from '@src/app/recipe/utils';
import { removePrivateProperties } from '@kemu-io/kemu-core/common/utils';

/**
 * Compares two values for deep equality while ignoring private (`$$`) keys.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const deepEqualIgnoringPrivate = (a: any, b: any): boolean => {
  try {
    return JSON.stringify(removePrivateProperties(a)) === JSON.stringify(removePrivateProperties(b));
  } catch {
    return false;
  }
};

/**
 * Builds a unique key for a child link.
 */
const childKey = (c: GateChild): string => `${c.childId}|${c.sourcePort}|${c.targetPort}`;

/**
 * Applies canvas info changes for recipe and per-thing level.
 */
const reconcileCanvasInfo = (recipePoolId: string, current: Recipe, target: Recipe): void => {
  const currentCanvas = current.canvas as RecipeCanvasInfo | undefined;
  const targetCanvas = target.canvas as RecipeCanvasInfo | undefined;
  if (!deepEqualIgnoringPrivate(currentCanvas, targetCanvas)) {
    setCanvasInfo(recipePoolId, JSON.parse(JSON.stringify(targetCanvas || {})) as RecipeCanvasInfo);
  }

  const allThingIds = new Set<string>([...Object.keys(current.blocks), ...Object.keys(target.blocks)]);
  allThingIds.forEach((thingId) => {
    const currentThing = current.blocks[thingId];
    const targetThing = target.blocks[thingId];
    if (!currentThing || !targetThing) { return; }
    if (!deepEqualIgnoringPrivate(currentThing.canvas, targetThing.canvas)) {
      setBlockCanvasInfo(recipePoolId, thingId, JSON.parse(JSON.stringify(targetThing.canvas)));
    }
  });
};

/**
 * Reconciles connections for a single widget by diffing current vs target children.
 */
const reconcileConnections = (recipePoolId: string, thingId: string, currentWidget: RecipeWidget, targetWidget: RecipeWidget): void => {
  const currentSet = new Set((currentWidget.children || []).map(childKey));
  const targetSet = new Set((targetWidget.children || []).map(childKey));

  // Remove edges present in current but not in target
  for (const key of currentSet) {
    if (!targetSet.has(key)) {
      const [childId, sourcePort, targetPort] = key.split('|');
      removeChildGate(recipePoolId, thingId, currentWidget.id, childId, sourcePort, targetPort);
    }
  }

  // Add edges present in target but not in current
  for (const key of targetSet) {
    if (!currentSet.has(key)) {
      const [childId, sourcePort, targetPort] = key.split('|');
      registerChildGate(recipePoolId, thingId, currentWidget.id, childId, sourcePort, targetPort);
    }
  }
};

/**
 * Reconciles fields of an existing widget (canvas, disabled, groupId, and state).
 */
const reconcileWidgetFields = async (
  recipePoolId: string,
  thingId: string,
  currentWidget: RecipeWidget,
  targetWidget: RecipeWidget,
  movedOrResizedIds: Set<string>,
): Promise<void> => {
  // Canvas
  if (!deepEqualIgnoringPrivate(currentWidget.canvas, targetWidget.canvas)) {
    setGateCanvasInfo(recipePoolId, thingId, currentWidget.id, JSON.parse(JSON.stringify(targetWidget.canvas)) as GateCanvasInfo);
    movedOrResizedIds.add(currentWidget.id);
  }

  // Disabled
  const currentDisabled = getWidgetDisabled(recipePoolId, thingId, currentWidget.id);
  const targetDisabled = !!targetWidget.disabled;
  if (currentDisabled !== targetDisabled) {
    disableWidget(recipePoolId, thingId, currentWidget.id, targetDisabled);
  }

  // Group
  if (currentWidget.groupId !== targetWidget.groupId) {
    currentWidget.groupId = targetWidget.groupId;
  }

  // State
  if (!deepEqualIgnoringPrivate(currentWidget.state, targetWidget.state)) {
    if (targetWidget.type === WidgetType.widgetGroup) {
      editCustomWidgetState(
        recipePoolId,
        thingId,
        currentWidget.id,
        JSON.parse(JSON.stringify(targetWidget.state)) as WidgetState<WidgetGroupState>,
        true,
      );
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      kemuCore.setGateState(recipePoolId, thingId, currentWidget.id, JSON.parse(JSON.stringify(targetWidget.state)) as any, true);
    }
    // Some widgets store size/visuals inside state (e.g., text). Mark for revalidate.
    movedOrResizedIds.add(currentWidget.id);
  }
};

/**
 * Adds a widget to the current recipe preserving its id and state.
 */
const addWidgetPreservingId = (currentThing: RecipeThing, widgetFromSnapshot: RecipeWidget): RecipeWidget => {
  // Deep clone snapshot widget to avoid aliasing
  const clone = JSON.parse(JSON.stringify(widgetFromSnapshot)) as RecipeWidget;
  currentThing.gates[clone.id] = clone;
  return currentThing.gates[clone.id];
};

/**
 * Restores recipe state in-place by computing diffs between the current cached recipe and the target snapshot.
 * Ordering: disconnect/remove → add/init → connect → mutate canvas/state/meta.
 */
export const reconcileInPlace = async (config: { recipePoolId: string; snapshot: Snapshot }): Promise<{ revalidateByThing: Record<string, string[]> }> => {
  const { recipePoolId, snapshot } = config;
  const current = findRecipe(recipePoolId);
  if (!current) { throw new Error(`Recipe [${recipePoolId}] not found`); }
  const target = snapshot.recipeObject as Recipe;

  // 1) Remove widgets not present in target (per thing)
  const allThingIds = new Set<string>([...Object.keys(current.blocks), ...Object.keys(target.blocks)]);
  const revalidateByThing: Record<string, string[]> = {};
  for (const thingId of allThingIds) {
    const currentThing = current.blocks[thingId];
    const targetThing = target.blocks[thingId];
    if (!currentThing && !targetThing) { continue; }

    if (currentThing && !targetThing) {
      // Entire thing removed in target: remove all widgets in current thing
      const currentWidgetIds = Object.keys(currentThing.gates);
      for (const wid of currentWidgetIds) {
        await removeWidget(recipePoolId, thingId, wid);
      }
      continue;
    }

    if (!currentThing && targetThing) {
      // Thing added in target: create thing by adding all widgets
      current.blocks[thingId] = JSON.parse(JSON.stringify(targetThing)) as RecipeThing;
      // Initialize newly added widgets
      for (const wid of Object.keys(targetThing.gates)) {
        await kemuCore.initializeWidget({ recipePoolId, thingRecipeId: thingId, widgetRecipeId: wid });
      }
      continue;
    }

    // Both exist → diff widgets
    const currentWidgetIds = new Set<string>(Object.keys(currentThing.gates));
    const targetWidgetIds = new Set<string>(Object.keys(targetThing.gates));

    // Disconnect edges to be removed is handled implicitly by removeWidget, but we still remove widgets explicitly
    for (const wid of currentWidgetIds) {
      if (!targetWidgetIds.has(wid)) {
        await removeWidget(recipePoolId, thingId, wid);
      }
    }

    // Add missing widgets (preserve id/state)
    const addedWidgets: RecipeWidget[] = [];
    for (const wid of targetWidgetIds) {
      if (!currentWidgetIds.has(wid)) {
        const added = addWidgetPreservingId(currentThing, targetThing.gates[wid]);
        addedWidgets.push(added);
      }
    }

    // Initialize added widgets
    for (const w of addedWidgets) {
      try {
        await kemuCore.initializeWidget({ recipePoolId, thingRecipeId: thingId, widgetRecipeId: w.id });
      } catch (e) {
        // Best-effort init; continue
        // eslint-disable-next-line no-console
        console.error('Error initializing widget during reconciliation', e);
      }
    }

    // Reconcile existing widgets (fields and connections)
    const movedOrResized = new Set<string>();
    for (const wid of targetWidgetIds) {
      const currentWidget = currentThing.gates[wid];
      const targetWidget = targetThing.gates[wid];
      if (!currentWidget || !targetWidget) { continue; }

      // Connections
      reconcileConnections(recipePoolId, thingId, currentWidget, targetWidget);
      // Fields
      await reconcileWidgetFields(recipePoolId, thingId, currentWidget, targetWidget, movedOrResized);
    }

    if (movedOrResized.size) {
      revalidateByThing[thingId] = Array.from(movedOrResized);
    }
  }

  // 2) Recipe-level updates: meta and canvas
  if (current.name !== target.name) {
    updateRecipeCacheMeta({ recipePoolId, meta: { name: target.name } });
  }

  // TODO: potentially update the DB id and author info too
  reconcileCanvasInfo(recipePoolId, current, target);
  return { revalidateByThing };
};

export default reconcileInPlace;


